/*
 * Sample findings for the Reminder gaps preview (outside production, or when the operator's
 * reminder_gaps_enabled is unset). Synthetic: the same fictional Rome operator and products as the
 * training inbox's sample data -- no real operator's or guest's data. Same shapes as the API
 * (cplanetbackend/apiv2/views/edit/reminder_gaps.py gap_data). The last two show the Requested and
 * Live states.
 */
const P = {
  colosseum: { id: 101, name: 'Colosseum Underground & Ancient Rome' },
  vatican: { id: 102, name: 'Vatican Museums & Sistine Chapel' },
  food: { id: 103, name: 'Trastevere Food Tour' },
  pompeii: { id: 104, name: 'Pompeii & Amalfi Coast Day Trip' },
  twilight: { id: 105, name: 'Rome at Twilight Walking Tour' },
};

const base = {
  verdict: 'unclear',
  current_text: '',
  answers: {},
  requested_text: '',
  edited: false,
  languages: [],
  requested_by: null,
  requested_ts: null,
  live_marked_ts: null,
  thread: [],
  verify: {},
  measurement: {},
};

const productTarget = (tagName, attr) => ({ type: 'tag_attr', tag_name: tagName, category: 'product_info', attr, lang: 'en' });

export const SAMPLE_GAPS = [
  {
    ...base,
    id: 1,
    kind: 'template',
    status: 'open',
    status_group: 'open',
    topic: 'find_guide_on_the_day',
    topic_label: 'Finding the guide on the day',
    title: 'Guests at the meeting point can’t spot the guide',
    what_guests_need: 'How to recognise the guide on arrival, and a number to call or message if they can’t find the group.',
    tour: null,
    evidence: {
      questions: 412,
      per_100: 2.6,
      after: { '2h': 371, '20h': 41 },
      received: [
        {
          kind: '2h',
          ts: '2026-10-05T07:00:00Z',
          template: '2-Hr Reminder - Chat',
          text: 'Hi [guest name],\n\nYour Colosseum Underground & Ancient Rome tour starts in 2 hours!\n\n📍 Meeting point: Piazza del Colosseo, by the exit of the Colosseo metro station\n⏰ Please arrive 15 minutes before the start\n📌 [link: maps.app.goo.gl]\n\nSee you soon!',
        },
        {
          kind: '20h',
          ts: '2026-10-04T13:00:00Z',
          template: '20-Hr Reminder - Chat',
          text: 'Hi [guest name],\n\nWe hope you’re looking forward to your Colosseum Underground & Ancient Rome tour tomorrow!\n\n📍 Meeting point: Piazza del Colosseo\n✅ Check-in: 15 minutes before the start\n📲 Your tickets and tomorrow’s details are in the app: [link: app.example.com]',
        },
      ],
      samples: ['We’re here, where are you?', 'Lots of groups here — what does our guide look like?', 'Is there a number we can call? Can’t see anyone'],
      tours: [
        { name: P.colosseum.name, questions: 164 },
        { name: P.vatican.name, questions: 131 },
        { name: P.twilight.name, questions: 117 },
      ],
    },
    target: { type: 'template', event_code: 'tour_participant.trip_flag_event_2h' },
    suggestion: {
      template: 'How to spot us: your guide carries {1} and wears {2}. Can’t find us? Call or WhatsApp {3}.',
      blanks: [
        { label: 'Your guide carries', options: ['an orange flag', 'a sign with our logo'], default: '' },
        { label: 'and wears', options: ['an orange T-shirt', 'a branded jacket'], default: '' },
        { label: 'On-the-day contact number', options: [], default: '' },
      ],
      position: { reminder: '2h', type: 'after', text: '📍 Meeting point: Piazza del Colosseo, by the exit of the Colosseo metro station' },
    },
  },
  {
    ...base,
    id: 2,
    kind: 'product',
    status: 'open',
    status_group: 'open',
    verdict: 'missing',
    topic: 'tickets_voucher_or_entry',
    topic_label: 'Tickets and entry',
    title: 'Guests don’t know if Pompeii entry is included',
    what_guests_need: 'Whether the Pompeii entrance ticket is included in the price and how they get it.',
    tour: P.pompeii,
    evidence: {
      questions: 58,
      per_100: 3.1,
      after: { '20h': 37, '2h': 21 },
      received: [
        {
          kind: '20h',
          ts: '2026-10-04T13:00:00Z',
          template: '20-Hr Reminder - Chat',
          text: 'Hi [guest name],\n\nTomorrow is your Pompeii & Amalfi Coast Day Trip!\n\n🚌 Pickup: from your hotel lobby — please be ready on time\n🎒 Bring water, sunscreen and comfortable shoes\n\nSee you tomorrow!',
        },
        {
          kind: '2h',
          ts: '2026-10-05T05:30:00Z',
          template: '2-Hr Reminder - Chat',
          text: 'Hi [guest name], your Pompeii & Amalfi Coast Day Trip starts soon. Please be ready for pickup. Have a great day!',
        },
      ],
      samples: ['Do we need to buy the Pompeii tickets ourselves?', 'Is the entrance fee for Pompeii included?', 'Where do we get our tickets for the ruins?'],
    },
    current_text: '',
    target: productTarget('104-en', 'reminders_20_hr'),
    suggestion: {
      template: 'Your Pompeii entrance ticket is {1}, and your guide {2}.',
      blanks: [
        { label: 'The entrance ticket is', options: ['included', 'not included'], default: '' },
        { label: 'Your guide', options: ['hands it out at the gate', 'sends it in the app the night before'], default: '' },
      ],
    },
  },
  {
    ...base,
    id: 3,
    kind: 'product',
    status: 'open',
    status_group: 'open',
    topic: 'meeting_point',
    topic_label: 'Meeting point',
    title: 'The meeting point wording sends guests into the metro',
    what_guests_need: 'That the group meets on the square above the Colosseo metro, not inside the station.',
    tour: P.colosseum,
    evidence: {
      questions: 47,
      per_100: 2.4,
      after: { '2h': 30, '20h': 17 },
      received: [
        {
          kind: '2h',
          ts: '2026-10-05T07:00:00Z',
          template: '2-Hr Reminder - Chat',
          text: 'Hi [guest name],\n\nYour Colosseum Underground & Ancient Rome tour starts in 2 hours!\n\n📍 Meeting point: Colosseo metro station, line B.\n⏰ Please arrive 15 minutes early\n\nSee you soon!',
        },
        {
          kind: '20h',
          ts: '2026-10-04T13:00:00Z',
          template: '20-Hr Reminder - Chat',
          text: 'Hi [guest name],\n\nTomorrow is your Colosseum Underground & Ancient Rome tour!\n\n📍 Meeting point: Colosseo metro station, line B.\n📲 Your tickets are in the app: [link: app.example.com]',
        },
      ],
      samples: ['We’re at the metro, which exit?', 'Do we meet inside the station?', 'Can’t find the meeting point, I’m by the ticket machines'],
    },
    current_text: 'Meeting point: Colosseo metro station, line B.',
    target: productTarget('101-en', 'meet1_name'),
    suggestion: {
      template: 'Wait {1}: don’t go down into the metro station.',
      blanks: [
        { label: 'Where to wait', options: ['on the square in front of the station exit', 'at the top of the metro steps'], default: '' },
      ],
      position: { reminder: '2h', type: 'after', text: '📍 Meeting point: Colosseo metro station, line B.' },
    },
  },
  {
    ...base,
    id: 4,
    kind: 'product',
    status: 'open',
    status_group: 'open',
    verdict: 'missing',
    topic: 'dress_code_or_what_to_bring',
    topic_label: 'Dress code and what to bring',
    title: 'Guests ask about the dress code',
    what_guests_need: 'What to wear to be let into the Sistine Chapel and St. Peter’s.',
    tour: P.vatican,
    evidence: {
      questions: 36,
      per_100: 1.8,
      after: { '20h': 29, '2h': 7 },
      received: [
        {
          kind: '20h',
          ts: '2026-10-04T13:00:00Z',
          template: '20-Hr Reminder - Chat',
          text: 'Hi [guest name],\n\nTomorrow is your Vatican Museums & Sistine Chapel tour!\n\n📍 Meeting point: Via Germanico 8, outside the café\n✅ Check-in: 15 minutes before the start\n🎟️ Your entry tickets are included: your guide hands them out\n\nSee you tomorrow!',
        },
        {
          kind: '2h',
          ts: '2026-10-05T07:00:00Z',
          template: '2-Hr Reminder - Chat',
          text: 'Hi [guest name], your Vatican Museums & Sistine Chapel tour starts in 2 hours. See you at Via Germanico 8!',
        },
      ],
      samples: ['Can I wear shorts to the Vatican?', 'Do my shoulders need to be covered?', 'Is there a dress code for the chapel?'],
    },
    target: productTarget('102-en', 'reminders_20_hr'),
    suggestion: {
      template: 'To enter the Sistine Chapel and St. Peter’s, please cover {1}.',
      blanks: [
        { label: 'Guests must cover', options: ['shoulders and knees', 'shoulders, knees and midriff'], default: '' },
      ],
    },
  },
  {
    ...base,
    id: 5,
    kind: 'template',
    status: 'open',
    status_group: 'open',
    topic: 'start_or_checkin_time',
    topic_label: 'Start or check-in time',
    title: 'Guests unsure whether the time is the start or the check-in',
    what_guests_need: 'Which time to arrive by when the reminder shows both the tour start and a check-in time.',
    tour: null,
    evidence: {
      questions: 33,
      per_100: 0.9,
      after: { '20h': 22, '2h': 11 },
      received: [
        {
          kind: '20h',
          ts: '2026-10-04T13:00:00Z',
          template: '20-Hr Reminder - Chat',
          text: 'Hi [guest name],\n\nWe hope you’re looking forward to your Colosseum Underground & Ancient Rome tour tomorrow!\n\n✅ Check-in: 8:45 AM\n⏰ Tour start: 9:00 AM\n📍 Meeting point: Piazza del Colosseo\n\nSee you tomorrow!',
        },
        {
          kind: '2h',
          ts: '2026-10-05T06:45:00Z',
          template: '2-Hr Reminder - Chat',
          text: 'Hi [guest name], your tour starts at 9:00 AM. See you at Piazza del Colosseo!',
        },
      ],
      samples: ['Is 9:00 when we meet or when it starts?', 'Should we arrive 15 minutes early?', 'What time do we need to be there?'],
      tours: [
        { name: P.colosseum.name, questions: 14 },
        { name: P.pompeii.name, questions: 11 },
        { name: P.food.name, questions: 8 },
      ],
    },
    target: { type: 'template', event_code: 'tour_participant.trip_flag_event_20h' },
    suggestion: {
      template: 'Please arrive by {1}: the tour leaves on time.',
      blanks: [
        { label: 'Arrive by', options: ['the check-in time', '15 minutes before the start'], default: '' },
      ],
    },
  },
  {
    ...base,
    id: 6,
    kind: 'app',
    status: 'open',
    status_group: 'open',
    verdict: 'covered',
    topic: 'meeting_point',
    topic_label: 'Meeting point',
    title: 'Guests look for the meeting point days before the reminder',
    what_guests_need: 'The meeting point as soon as they book — the reminders give it, but guests ask after the 7-day message.',
    tour: P.food,
    evidence: {
      questions: 29,
      per_100: 2.0,
      after: { '7d': 21, postbooking3h: 8 },
      received: [
        {
          kind: '7d',
          ts: '2026-09-28T10:00:00Z',
          template: '1-Week Reminder - Chat',
          text: 'Hi [guest name],\n\nThanks for booking the Trastevere Food Tour! 🍝\n\n📲 Everything you need for the day — meeting point, map and your guide’s details — will be in the app: [link: app.example.com]\n\nTell us about any dietary requirements by replying to this message.',
        },
      ],
      samples: ['Where do we meet for the food tour?', 'I can’t see the meeting point in the app yet', 'Hi, just checking the address for Saturday'],
    },
    target: { type: 'app' },
    suggestion: {
      template: 'Add the meeting point to the 7-day message: {1}.',
      blanks: [{ label: 'Meeting point', options: [], default: '' }],
    },
  },
  {
    ...base,
    id: 7,
    kind: 'product',
    status: 'requested',
    status_group: 'requested',
    topic: 'duration_or_end_time',
    topic_label: 'Duration and end time',
    title: 'Guests ask what time the tour finishes',
    what_guests_need: 'How long the twilight walk lasts and where it ends.',
    tour: P.twilight,
    evidence: {
      questions: 18,
      per_100: 1.5,
      after: { '20h': 12, '2h': 6 },
      received: [
        {
          kind: '20h',
          ts: '2026-10-04T13:00:00Z',
          template: '20-Hr Reminder - Chat',
          text: 'Hi [guest name],\n\nTomorrow is your Rome at Twilight Walking Tour!\n\n📍 Meeting point: Campo de’ Fiori, by the statue\n⏰ Tour start: 7:30 PM\n\nSee you tomorrow!',
        },
        {
          kind: '2h',
          ts: '2026-10-05T15:30:00Z',
          template: '2-Hr Reminder - Chat',
          text: 'Hi [guest name], your Rome at Twilight Walking Tour starts at 7:30 PM in Campo de’ Fiori. See you soon!',
        },
      ],
      samples: ['What time does the tour end?', 'Where do we finish? We have a dinner booking'],
    },
    target: productTarget('105-en', 'reminders_20_hr'),
    suggestion: {
      template: 'The tour lasts about {1} and ends {2}.',
      blanks: [
        { label: 'Length', options: ['2 hours', '2.5 hours'], default: '' },
        { label: 'It ends', options: ['at the Trevi Fountain', 'at Piazza Navona'], default: '' },
      ],
    },
    answers: { 1: '2 hours', 2: 'at Piazza Navona' },
    requested_text: 'The tour lasts about 2 hours and ends at Piazza Navona, close to plenty of restaurants.',
    edited: true,
    languages: ['all'],
    requested_by: { id: 1, name: 'Giulia' },
    requested_ts: '2026-10-06T14:02:00Z',
    thread: [{ who: 'ops', ts: '2026-10-06T15:10:00Z', text: 'Should this also go in the 2-hour reminder?' }],
  },
  {
    ...base,
    id: 8,
    kind: 'product',
    status: 'live',
    status_group: 'live',
    verdict: 'missing',
    topic: 'food_drink',
    topic_label: 'Food and drink',
    title: 'Guests ask about vegetarian and gluten-free options',
    what_guests_need: 'Whether dietary needs are catered for and how to tell the guide.',
    tour: P.food,
    evidence: {
      questions: 24,
      per_100: 1.7,
      after: { '20h': 24 },
      received: [
        {
          kind: '20h',
          ts: '2026-10-04T13:00:00Z',
          template: '20-Hr Reminder - Chat',
          text: 'Hi [guest name],\n\nTomorrow is your Trastevere Food Tour! 🍝\n\n📍 Meeting point: Piazza di Santa Maria in Trastevere, by the fountain\n⏰ Tour start: 11:00 AM\n\nCome hungry!',
        },
      ],
      samples: ['Are there vegetarian options?', 'My daughter is coeliac, is that ok?'],
    },
    target: productTarget('103-en', 'reminders_20_hr'),
    requested_text: 'Vegetarian and gluten-free tastings are available: just tell your guide at check-in.',
    languages: ['all'],
    requested_by: { id: 1, name: 'Giulia' },
    requested_ts: '2026-09-10T09:00:00Z',
    live_marked_ts: '2026-09-12T10:00:00Z',
    verify: { en: { seen: 312, sent: 312 }, fr: { seen: 41, sent: 41 }, es: { seen: 0, sent: 18 } },
    suggestion: {},
  },
];
